import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Bookmark, Download, Copy, Trash2, Mail, ExternalLink,
  Loader2, FileSpreadsheet, User, Pencil, Check, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STATUS_META = {
  new:       { label: "New",       dot: "#64748b", bg: "bg-slate-50 text-slate-700 border-slate-200" },
  contacted: { label: "Contacted", dot: "#f68b1f", bg: "bg-tc-cream text-tc-ink border-tc-cream" },
  replied:   { label: "Replied",   dot: "#7ac143", bg: "bg-green-50 text-green-700 border-green-200" },
  dead:      { label: "Dead",      dot: "#94a3b8", bg: "bg-slate-100 text-slate-500 border-slate-200" },
};
const STATUS_ORDER = ["new", "contacted", "replied", "dead"];

function StatusPill({ value, onChange, testid }) {
  const meta = STATUS_META[value] || STATUS_META.new;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`h-7 rounded-full border px-2.5 pr-7 text-[11px] font-bold uppercase tracking-wider appearance-none cursor-pointer ${meta.bg}`}
      style={{ backgroundImage: "none" }}
      title="Update status"
    >
      {STATUS_ORDER.map((s) => (
        <option key={s} value={s}>{STATUS_META[s].label}</option>
      ))}
    </select>
  );
}

function LeadRow({ lead, onPatch, onDelete, onDraft, draftingKey }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(lead.note || "");
  const meta = STATUS_META[lead.status] || STATUS_META.new;

  const saveNote = async () => {
    await onPatch(lead.id, { note: noteDraft });
    setNoteOpen(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 overflow-hidden"
      data-testid={`saved-lead-${lead.name}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-tc-ink text-lg truncate">{lead.name}</span>
            {lead.website && (
              <a href={lead.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-tc-orange hover:underline text-xs">
                <ExternalLink className="w-3 h-3" /> site
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1 flex-wrap">
            {lead.sector && <span className="px-2 py-0.5 rounded-full bg-slate-100 font-bold uppercase tracking-wide">{lead.sector}</span>}
            {lead.size_signal && <span>{lead.size_signal}</span>}
            <span className="text-slate-400">· saved {(lead.saved_at || "").slice(0,10)}</span>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <StatusPill
            value={lead.status || "new"}
            onChange={(v) => onPatch(lead.id, { status: v })}
            testid={`saved-status-${lead.name}`}
          />
          <button
            onClick={() => onDelete(lead)}
            className="h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-red-500 hover:border-red-200 flex items-center justify-center"
            title="Remove from list"
            data-testid={`saved-remove-${lead.name}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {lead.why_this_fits && (
        <p className="text-sm text-slate-700 mt-3 leading-relaxed">{lead.why_this_fits}</p>
      )}

      {Array.isArray(lead.contacts) && lead.contacts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">People</div>
          <ul className="space-y-2">
            {lead.contacts.map((c, i) => {
              const key = `${lead.id}::${c.name}`;
              return (
                <li key={i} className="flex items-center justify-between gap-2 bg-tc-cream/40 border border-tc-cream rounded-xl px-2.5 py-2 overflow-hidden">
                  <div className="min-w-0 flex-1 flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-tc-ink text-sm truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.title}
                        {c.source_url && (
                          <>
                            {" · "}
                            <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-tc-cyan hover:underline">source</a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onDraft(lead, c)}
                    disabled={draftingKey === key}
                    data-testid={`saved-draft-${lead.name}-${i}`}
                    className="shrink-0 h-8 px-2.5 rounded-lg text-white font-bold text-xs flex items-center gap-1 hover:brightness-110 disabled:opacity-60"
                    style={{ background: "#f68b1f" }}
                    title={`Draft email to ${c.name}`}
                  >
                    {draftingKey === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">Draft to </span>{c.name.split(" ")[0]}
                  </button>
                </li>
              );
            })}
          </ul>
          {(!lead.contacts || lead.contacts.length === 0) ? null : (
            <button
              onClick={() => onDraft(lead, null)}
              disabled={draftingKey === `${lead.id}::__generic__`}
              className="mt-2 text-xs text-slate-500 hover:text-tc-orange inline-flex items-center gap-1 font-semibold disabled:opacity-60"
            >
              {draftingKey === `${lead.id}::__generic__` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              Or draft a general outreach to the org
            </button>
          )}
        </div>
      )}

      {(!lead.contacts || lead.contacts.length === 0) && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <button
            onClick={() => onDraft(lead, null)}
            disabled={draftingKey === `${lead.id}::__generic__`}
            className="h-9 px-3 rounded-xl text-white font-bold text-xs flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60"
            style={{ background: "#f68b1f" }}
            data-testid={`saved-draft-generic-${lead.name}`}
          >
            {draftingKey === `${lead.id}::__generic__` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Draft outreach
          </button>
        </div>
      )}

      {/* Note area */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">My note</div>
        {!noteOpen ? (
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-slate-600 whitespace-pre-wrap min-h-[20px]">
              {lead.note || <span className="text-slate-400 italic">No note yet.</span>}
            </p>
            <button
              onClick={() => { setNoteDraft(lead.note || ""); setNoteOpen(true); }}
              className="shrink-0 h-7 px-2 rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-slate-300 text-xs font-bold inline-flex items-center gap-1"
              data-testid={`saved-note-edit-${lead.name}`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              rows={3}
              maxLength={800}
              placeholder="e.g., Emailed Jane 10/14 — proposed intro call for the week of 10/21."
              className="w-full resize-none rounded-xl border-2 border-slate-200 focus:border-tc-orange outline-none px-3 py-2 text-sm"
              data-testid={`saved-note-input-${lead.name}`}
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setNoteOpen(false)}
                className="h-8 px-3 rounded-lg border border-slate-200 text-slate-500 hover:border-slate-300 text-xs font-bold inline-flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button
                onClick={saveNote}
                className="h-8 px-3 rounded-lg bg-tc-ink text-white hover:brightness-110 text-xs font-bold inline-flex items-center gap-1"
                data-testid={`saved-note-save-${lead.name}`}
              >
                <Check className="w-3.5 h-3.5" /> Save note
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function SavedLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null); // null = loading
  const [statusFilter, setStatusFilter] = useState("all");
  const [draftingKey, setDraftingKey] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/saved-leads");
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Couldn't load your saved leads.");
      setLeads([]);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!leads) return null;
    if (statusFilter === "all") return leads;
    return leads.filter((l) => (l.status || "new") === statusFilter);
  }, [leads, statusFilter]);

  const statusCounts = useMemo(() => {
    const base = { all: 0, new: 0, contacted: 0, replied: 0, dead: 0 };
    (leads || []).forEach((l) => {
      base.all += 1;
      base[l.status || "new"] = (base[l.status || "new"] || 0) + 1;
    });
    return base;
  }, [leads]);

  const patchLead = async (id, body) => {
    try {
      const { data } = await api.patch(`/saved-leads/${id}`, body);
      setLeads((prev) => (prev || []).map((l) => l.id === id ? data : l));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update that lead.");
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Remove "${lead.name}" from your Saved Leads?`)) return;
    try {
      await api.delete(`/saved-leads/${lead.id}`);
      setLeads((prev) => (prev || []).filter((l) => l.id !== lead.id));
      toast.success(`Removed ${lead.name}.`);
    } catch (err) {
      toast.error("Couldn't remove that lead.");
    }
  };

  const draftEmail = async (lead, contact) => {
    const key = `${lead.id}::${contact ? contact.name : "__generic__"}`;
    setDraftingKey(key);
    try {
      const { data: session } = await api.post("/email-copilot/sessions");
      const parts = [];
      if (contact) {
        parts.push(`Write a single cold outreach email addressed to ${contact.name}, ${contact.title} at ${lead.name}.`);
      } else {
        parts.push(
          `Write a single cold outreach email to ${lead.name}` +
          (lead.sector ? ` (${lead.sector})` : "") +
          ` to the most senior HR / People / Culture leader there.`
        );
      }
      if (lead.website) parts.push(`Website: ${lead.website}.`);
      if (lead.why_this_fits) parts.push(`Reason to engage: ${lead.why_this_fits}`);
      if (lead.note) parts.push(`Context from my notes: ${lead.note}`);
      if (lead.sources?.length) {
        parts.push(`Reference signals: ${lead.sources.slice(0, 2).map((s) => s.url).join(", ")}`);
      }
      parts.push(`Keep it 120 to 160 words, warm and specific. Use the standard structure and ONE approved CTA. Do not invent prior contact.`);
      const seed = parts.join(" ");
      const title = contact ? `${lead.name} — ${contact.name}` : `${lead.name} — outreach`;
      await api.patch(`/email-copilot/sessions/${session.id}`, { title }).catch(() => {});
      navigate(`/email-copilot?seed=${encodeURIComponent(seed)}&session=${session.id}`);
    } catch (err) {
      toast.error("Couldn't open CJ for this lead.");
    } finally {
      setDraftingKey(null);
    }
  };

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const qp = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const resp = await api.get(`/saved-leads/export.csv${qp}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      const scope = statusFilter === "all" ? "all" : statusFilter;
      a.download = `cj-saved-leads-${scope}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Couldn't export. Try again?");
    } finally {
      setExporting(false);
    }
  };

  const copyAsList = async () => {
    const rows = filtered || [];
    if (rows.length === 0) {
      toast.info("Nothing to copy yet.");
      return;
    }
    const lines = rows.map((l) => {
      const first = (l.contacts || [])[0];
      const contactStr = first ? ` — ${first.name} (${first.title})` : "";
      const site = l.website ? ` — ${l.website}` : "";
      return `• ${l.name}${contactStr}${site}`;
    });
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success(`Copied ${rows.length} lead${rows.length === 1 ? "" : "s"} to clipboard.`);
    } catch (err) {
      toast.error("Clipboard not available — try the CSV export.");
    }
  };

  return (
    <motion.div
      className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 pb-12 space-y-5"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="saved-leads-page"
    >
      <div className="flex items-center justify-between gap-3">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-tc-ink" data-testid="saved-back">
          <ArrowLeft className="w-4 h-4" /> Back to Hub
        </Link>
        <Link to="/find-leads" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-tc-orange" data-testid="saved-find-more">
          Find more leads
        </Link>
      </div>

      <div>
        <div className="tc-eyebrow flex items-center gap-2 text-slate-400"><Bookmark size={14} /> Saved Leads</div>
        <h1 className="font-display text-3xl md:text-4xl font-black text-tc-ink mt-1">Your pipeline. Your pace.</h1>
        <p className="text-slate-600 mt-1">Everything you've saved from Find Leads. Keep notes, move things forward, export when you need a list in Slack or email.</p>
      </div>

      {/* Status tabs + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="saved-status-tabs">
          {["all", ...STATUS_ORDER].map((s) => {
            const active = statusFilter === s;
            const label = s === "all" ? "All" : STATUS_META[s].label;
            const count = statusCounts[s] || 0;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`saved-filter-${s}`}
                className={`h-9 px-3 rounded-full text-sm font-bold border transition-colors ${
                  active ? "bg-tc-ink text-white border-tc-ink" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                }`}
              >
                {label}
                <span className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                  active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                }`}>{count}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyAsList}
            disabled={!leads || leads.length === 0}
            data-testid="saved-copy-list"
            className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-700 hover:border-slate-300 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Copy className="w-4 h-4" /> Copy as list
          </button>
          <button
            onClick={downloadCsv}
            disabled={!leads || leads.length === 0 || exporting}
            data-testid="saved-export-csv"
            className="h-9 px-3 rounded-xl text-white font-bold text-sm hover:brightness-110 inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ background: "#083050" }}
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Export CSV
          </button>
        </div>
      </div>

      {/* List */}
      {leads === null ? (
        <div className="text-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <div className="text-sm">Loading your leads…</div>
        </div>
      ) : (filtered?.length ?? 0) === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center" data-testid="saved-empty">
          <Bookmark className="w-8 h-8 mx-auto text-slate-300" />
          <div className="mt-3 font-bold text-tc-ink">
            {statusFilter === "all" ? "No saved leads yet." : `Nothing in ${STATUS_META[statusFilter]?.label || statusFilter}.`}
          </div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {statusFilter === "all"
              ? "Head to Find Leads and tap the bookmark on any card. It'll land here, where you can note progress and export anytime."
              : "Change the filter above, or save more leads from Find Leads."}
          </p>
          <Link
            to="/find-leads"
            className="inline-flex items-center gap-2 mt-4 h-10 px-4 rounded-xl text-white font-bold text-sm"
            style={{ background: "#f68b1f" }}
          >
            Go to Find Leads
          </Link>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="saved-leads-list">
          <AnimatePresence initial={false}>
            {(filtered || []).map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                onPatch={patchLead}
                onDelete={deleteLead}
                onDraft={draftEmail}
                draftingKey={draftingKey}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
}
